/**
 * @file: This file holds the controller of the takeover panel.
 *
 * A takeover is the day's list plus the act's bumper, spread evenly and on the
 * first slot. The panel asks for the bumper and the name, and then writes the
 * copy: a playlist on the scene of the page, its rows in one call, and an
 * interval with the Takeover role when the operator gave the times.
 *
 * The panel opens inside the Recipe surface and not in a dialog, because a
 * dialog would cover the list the copy is made from (D2, D9). It uses the class
 * names of the Compose stylesheet, because it is a part of that page.
 *
 * The reason to change this file is a change of the writes. The form is in
 * `takeover-panel-state.js`, and the order of the copy is in `ComposeState`.
 */

import moment from 'moment'
import { ROLES } from 'common/interval-roles'
import { iconForMediaType } from 'common/media-icon'
import { screensOf } from '../managers/catalogue'
import { itemsOf } from '../managers/compose-state'
import { TakeoverPanelState } from './takeover-panel-state'

class TakeoverPanelController {
  constructor ($q, $log, $window, toaster, ApiService, SceneCollectionService) {
    'ngInject'

    this._$q = $q
    this._$log = $log
    this._$window = $window
    this._toaster = toaster
    this._ApiService = ApiService
    this._SceneCollectionService = SceneCollectionService

    // One write at a time. A second press must not start a second chain.
    this.creating = false
    this.form = new TakeoverPanelState()
  }

  $onInit () {
    this.refresh()
  }

  /**
   * The page changed the list the copy is made from.
   *
   * A command of the page and a late arrival of the library both change what
   * the panel offers, and the panel sees neither. The page counts its own
   * refreshes, and this rebuilds the panel on every count.
   */
  $onChanges (changes) {
    if (changes.revision && !changes.revision.isFirstChange()) {
      this.refresh()
    }
  }

  /**
   * The lists and the answers the panel draws.
   *
   * The reason a copy is refused is here and not read on every digest: it
   * scans every row and it reads two dates, and the template asks for it three
   * times. Every control that changes the answer calls this.
   */
  refresh () {
    this.view = {
      choices: this.source.bumperChoices(this.form.search, this._copyScreens()),
      replaceable: this.source.rowMedia(),
      problem: this.problem()
    }
  }

  /**
   * The screens a copy plays on. A copy is put on the scene of the page, so a
   * bumper and the copied rows are judged against that one alone.
   */
  _copyScreens () {
    return screensOf(null, this.scene)
  }

  get _sceneId () {
    return this.scene ? this.scene.id : null
  }

  // ===== The template calls these =====

  typeIcon (mediaType) {
    return iconForMediaType(mediaType)
  }

  selectBumper (media) {
    this.form.selectBumper(media, this.source.takeoverName(this.baseName, media.name))
    this.refresh()
  }

  touchName () {
    this.form.touchName()
    this.refresh()
  }

  /** A time changed, and the two times decide whether a copy can be made. */
  timesChanged () {
    this.refresh()
  }

  stepCount (delta) {
    this.form.stepCount(delta)
    this.refresh()
  }

  problem () {
    return this.form.problem({ hasScene: Boolean(this._sceneId), canCopy: this.source.canSave() })
  }

  close () {
    if (this.creating) {
      return
    }

    this.onClose()
  }

  /**
   * Create the takeover: a playlist on this scene, its rows in one write, and
   * an interval with the Takeover role when the operator gave the times. The
   * calendar owns time (D10), so the interval is an ordinary one afterwards.
   */
  create () {
    if (this.creating) {
      return this._$q.when()
    }

    const problem = this.problem()

    if (problem) {
      this._toaster.error(problem, 'Could not create the takeover')
      return this._$q.when()
    }

    // The copy holds what is on screen, and the list it came from keeps what is
    // stored. The operator has to know that the two now differ.
    if (this.source.dirty && !this._$window.confirm(
      'The takeover keeps your pins and spreads the other rows again. ' +
      'The changes to this playlist stay unsaved. Continue?'
    )) {
      return this._$q.when()
    }

    this._busy(true)

    const { rows } = this.source.takeoverRows(
      this.form.bumper,
      this.form.rowOptions(this._copyScreens())
    )

    return this._write(this.form.trimmedName(), rows)
      .catch(error => {
        this._busy(false)
        this._$log.error(`[TakeoverPanel] create: ${error.message}`, error)
        this._toaster.error(error.message, 'Could not create the takeover')
      })
  }

  _busy (creating) {
    this.creating = creating
    this.onBusy({ $busy: creating })
    this.refresh()
  }

  /**
   * The writes of a takeover, in order. The playlist has to exist before its
   * rows, and its rows before the interval that puts it on the screen.
   *
   * A failure after the playlist exists removes it again. An empty playlist left
   * on the scene for every attempt is worse than no copy at all.
   */
  _write (name, rows) {
    return this._SceneCollectionService.create({ name })
      .then(collection => this._SceneCollectionService
        .addSceneCollectionToScene(this._sceneId, collection.id)
        .then(() => this._ApiService.call('scenecollectionitem/setSceneCollectionItems', {
          sceneCollectionId: collection.id,
          items: itemsOf(rows)
        }))
        .catch(error => this._removeHalfMade(collection.id, error))
        .then(() => this._schedule(collection.id))
        .then(() => this._created(collection, name, rows.length)))
  }

  /**
   * Remove a playlist whose rows could not be written, and report the first
   * failure. A failed removal is not reported: the operator cannot act on it,
   * and the reason the copy failed is the useful message.
   */
  _removeHalfMade (sceneCollectionId, error) {
    return this._SceneCollectionService.delete(sceneCollectionId)
      .catch(removalError => {
        this._$log.error(`[TakeoverPanel] cleanup: ${removalError.message}`, removalError)
      })
      .then(() => this._$q.reject(error))
  }

  _created (collection, name, itemCount) {
    this._busy(false)
    this._toaster.success(`${itemCount} items in ${name}`, 'Takeover created')
    this.onCreated({ $collectionId: collection.id })
  }

  /**
   * Schedule the copy, when the operator gave both times.
   *
   * A conflict is possible, and the playlist already exists at this point. The
   * message is shown and the page still goes to the copy, so the operator can
   * fix the time in the calendar instead of building the list again.
   */
  _schedule (sceneCollectionId) {
    if (!this.form.isScheduled()) {
      return this._$q.when()
    }

    return this._SceneCollectionService.addInterval({
      sceneCollectionId,
      interval_start: moment(this.form.start).format(),
      interval_end: moment(this.form.end).format(),
      role: ROLES.takeover
    }).catch(error => {
      this._$log.error(`[TakeoverPanel] interval: ${error.message}`, error)
      this._toaster.error(error.message, 'The playlist was created, but not scheduled')
    })
  }
}

export default {
  bindings: {
    // The list the copy is made from. The panel reads its queries and never
    // changes it: the copy is a new playlist.
    source: '<',
    // The scene of the page, which is where the copy goes.
    scene: '<',
    // The name of the playlist the copy is made from.
    baseName: '<',
    // A number the page raises whenever the list or the library changed. The
    // panel then draws its lists again.
    revision: '<',
    onBusy: '&',
    onClose: '&',
    onCreated: '&'
  },
  controller: TakeoverPanelController,
  template: require('./takeover-panel.tpl.html').default
}

export { TakeoverPanelController }
